import React, { useState, useEffect } from "react";
import {
  Box,
  Grid,
  Typography,
  CircularProgress,
  Alert,
  ToggleButtonGroup,
  ToggleButton,
} from "@mui/material";
import SupersetEmbeddedDashboard from "../../components/SupersetEmbeddedDashboard";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useChosenEmployer } from "../../contexts/EmployerContext";
import HttpService from "../../services/HttpService";
import WidgetCard from "./WidgetCard";
import UserService from "../../services/UserService";

export default function Dashboard() {
  const { chosenEmployerId } = useChosenEmployer();
  const [timeRange, setTimeRange] = useState("30days");
  const [maximizedWidget, setMaximizedWidget] = useState(null);
  const queryClient = useQueryClient();

  const hasSuperAdminRole = UserService.hasRole([
    "pc-employer-super-admin",
    "home-dashboard",
    "ou-manager",
  ]);

  useEffect(() => {
    if (hasSuperAdminRole) {
      queryClient.invalidateQueries(["dashboardWidgets"]);
      queryClient.invalidateQueries(["widgetData"]);
    }
  }, [hasSuperAdminRole, queryClient]);

  // Fetch widget definitions
  async function fetchWidgets() {
    const response = await HttpService.getAxiosClient().get(
      window.API_URL + `/dashboard/widgets`
    );
    return response;
  }

  const {
    data: widgetsData,
    isLoading: isLoadingWidgets,
    isError: isErrorWidgets,
  } = useQuery(["dashboardWidgets"], fetchWidgets, {
    enabled: !!chosenEmployerId && hasSuperAdminRole,
    refetchOnWindowFocus: false,
    staleTime: 0,
  });

  const handleTimeRangeChange = (event, newRange) => {
    if (newRange !== null) {
      setTimeRange(newRange);
    }
  };

  const handleToggleMaximize = (widgetCode) => {
    setMaximizedWidget(maximizedWidget === widgetCode ? null : widgetCode);
  };

  if (!hasSuperAdminRole) {
    return null;
  }

  if (isErrorWidgets) {
    return (
      <Box p={3}>
        <Alert severity="error">Failed to load dashboard widgets</Alert>
      </Box>
    );
  }

  if (isLoadingWidgets) {
    return (
      <Box display="flex" justifyContent="center" p={5}>
        <CircularProgress />
      </Box>
    );
  }

  const widgets = widgetsData?.data || [];

  // Sort widgets so maximized widget appears first
  const sortedWidgets = [...widgets].sort((a, b) => {
    if (maximizedWidget === a.widgetCode) return -1;
    if (maximizedWidget === b.widgetCode) return 1;
    return 0; // Keep original order for non-maximized widgets
  });

  return (
    <Box>
      {widgets.length > 0 && (
        <Box
          mb={3}
          display="flex"
          justifyContent="space-between"
          alignItems="center"
        >
          <Typography variant="h5" component="h1">
            Your Business Snapshot
          </Typography>
          <ToggleButtonGroup
            value={timeRange}
            exclusive
            onChange={handleTimeRangeChange}
            size="small"
          >
            <ToggleButton value="today">Today</ToggleButton>
            <ToggleButton value="yesterday">Yesterday</ToggleButton>
            <ToggleButton value="7days">7 Days</ToggleButton>
            <ToggleButton value="30days">30 Days</ToggleButton>
            <ToggleButton value="90days">90 Days</ToggleButton>
            {/* <ToggleButton value="ytd">YTD</ToggleButton> */}
          </ToggleButtonGroup>
        </Box>
      )}

      <Grid container spacing={3}>
        {sortedWidgets.map((widget) => (
          <WidgetCard
            key={widget.widgetCode}
            widget={widget}
            timeRange={timeRange}
            isMaximized={maximizedWidget === widget.widgetCode}
            onToggleMaximize={() => handleToggleMaximize(widget.widgetCode)}
          />
        ))}
      </Grid>
    </Box>
  );
}
